"use client";
import type React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Close, Delete } from "@mui/icons-material";
import UploadDialog from "./UploadDialog";
import Cookies from "js-cookie";
import {
  axiosformdataInstance,
  axiosInstance,
} from "../../utils/tokenSetCookie";
import { useLanguageContext } from "../../components/language/languageContext";
import {
  MediaPlayer,
  type MediaPlayerInstance,
  MediaProvider,
  Poster,
} from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "../../styles/userPanel.scss";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "i18next";
import { isEqual } from "lodash";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const CUSTOM_UPLOAD_BASE_URL = import.meta.env.VITE_CUSTOM_UPLOAD_BASE_URL;
const VIDEO_BASE_URL = import.meta.env.VITE_VIDEO_BASE_URL;

type AnimationList = {
  id: number;
  animationPid: number;
  animationId: string;
  animationTagName: string;
  checked: boolean;
  type: string;
  videoUrl: string;
  homepageImageUrl: string;
  bannerUrl: string;
  uploadfrom?: string; //this key is used in unselect for image and  video () to list when unslect the playlist
  icon?: React.ReactNode;
  loopId?: number | undefined;
  customTitle?: string | undefined;
  customUrl?: string | undefined;
};
type AnimationListData = {
  expanded?: boolean;
  systemTagName: string;
  count: number;
  animationList: AnimationList[];
};

const LoopSetting: React.FC = () => {
  const {
    // defaultLanguage
  } = useLanguageContext();
  const { t } = useTranslation();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [dialogText, setDialogText] = useState("");

  const handleClose = () => {
    setOpen(false);
  };

  const [uploadType, setUploadType] = useState<"animation" | "image">(
    "animation"
  );
  const [uploadedVideo, _setUploadedVideo] = useState<{
    id: string;
    file: File;
    url: string;
  } | null>(null);
  const [uploadedImage, _setUploadedImage] = useState<{
    id: string;
    file: File;
    url: string;
  } | null>(null);

  // for API call manage state
  const [animationList, setAnimationList] = useState<AnimationListData[]>([]);
  const [newPlaylist, setNewPlaylist] = useState<AnimationList[]>([]); // Playlist state
  const [newSelectedVideo, setNewSelectedVideo] = useState<string | null>(null);
  const [newSelectedImage, setNewSelectedImage] = useState<string | null>(null);
  const [customEnable, setCustomEnable] = useState<boolean>(false);
  const [orderSequence, setOrderSequence] = useState<number[]>([]);
  const [_progress, _setProgress] = useState(0);
  const [_isUploading, setIsUploading] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState(
    Number(localStorage.getItem("fullscreenStatus"))
  );
  const [isImageSeconds, setIsImageSeconds] = useState(
    Number(localStorage.getItem("animationDuration"))
  );
  const [initialPlaylist, setInitialPlaylist] = useState<any>([null]);
  const [initialOrderSequence, setInitialOrderSequence] = useState<any>([null]);
  const [actionType, setActionType] = useState<"save" | "reset" | null>(null);
  const videoRef = useRef<MediaPlayerInstance | null>(null);

  // Initially get the animation list via Json
  useEffect(() => {
    // Function to fetch data
    const token = Cookies.get("userToken");
    const payload = {
      languageId: 50,
    };

    //  Function to fetch data for initial playlist
    const getAnimationDetail = async (payload: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "animation/loop-settings/list",
          payload
        );
        if (response.status >= 200 && response.status <= 300) {
          setAnimationList(response.data.data);
        }
      } catch (err) {
        console.log(err);
      }
    };
    if (token) {
      getAnimationDetail(payload); // Passing the payload as an argument
    }
  }, []); // Empty dependency array ensures it runs only once on page load

  // Change the seconds for showing images
  useEffect(() => {
    const payload = {
      webSiteId: 1,
      duration: Number(isImageSeconds),
      fullscreen: isFullscreen,
    };
    const changeLoopOptions = async (payload: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "animation/loop-settings/looped-animation-option",
          payload
        );
        if (response.status >= 200 && response.status <= 300) {
          getAnimationPlaylist();
        }
      } catch (err) {
        console.log(err);
      }
    };
    changeLoopOptions(payload); // Passing the payload as an argument
  }, [isFullscreen, isImageSeconds]);

  // if only one category available means, the list will expanded.
  useEffect(() => {
    if (animationList.length > 0) {
      setAnimationList((prevList) =>
        prevList.map((category) => ({
          ...category,
          expanded: true, // Auto-expand if only one category exists
        }))
      );
    }
  }, []);


    useEffect(() => {
      return () => {
        // Cleanup object URLs when component unmounts
        if (uploadedVideo?.url) URL.revokeObjectURL(uploadedVideo.url);
        if (uploadedImage?.url) URL.revokeObjectURL(uploadedImage.url);
      };
    }, []);

  // Check if the playlist and order sequence are the same
  const isDisabled = useMemo(() => {
    if (!initialPlaylist.length) {
      return true; // If the initial playlist is empty, keep the button disabled
    }

    const playlistEqual = isEqual(newPlaylist, initialPlaylist);
    const orderEqual = isEqual(orderSequence, initialOrderSequence);


    return playlistEqual && orderEqual;
  }, [newPlaylist, initialPlaylist, orderSequence, initialOrderSequence]);

  // Fetch Animation List (With animationId)
  const getCategoryAnimation = () => {
    const payload = {
      languageId: 50,
    };
    const getAnimationDetail = async (payload: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "animation/loop-settings/list",
          payload
        );
        if (response.status >= 200 && response.status <= 300) {
          setAnimationList(response.data.data);
        }
      } catch (err) {
        console.log(err);
      }
    };
    getAnimationDetail(payload); // Passing the payload as an argument
  };

  // Fetch Animation Playlist (With animationId)
  const getAnimationPlaylist = async () => {
    const payload = { webSiteId: 1 };

    try {
      const response = await axiosInstance.post(
        API_BASE_URL + "animation/loop-settings/selected-videos",
        payload
      );
      if (response.status >= 200 && response.status <= 300) {
        const fetchedPlaylist = response.data.data.loopedVideos;
        setNewPlaylist([...fetchedPlaylist]);
        setInitialPlaylist([...fetchedPlaylist]); // Store initial value for comparison
        // return fetchedPlaylist;
        const initialOrder = fetchedPlaylist.map(
          (item: { id: number }) => item.id
        );
        setOrderSequence(initialOrder);
        setInitialOrderSequence(initialOrder);
      }
    } catch (err) {
      console.log(err);
    }
    return [];
  };

  // Open dialog for save or reset
  const handleOpen = (type: "save" | "reset") => {
    setActionType(type); // Store action type
    if (type === "save") {
      setDialogText(t("are_you_sure_you_want_to_save_the_playlist"));

    } else if (type === "reset") {
      setDialogText(t("are_you_sure_you_want_to_reset_the_playlist"));
    }
    setOpen(true); // Open dialog
  };

  // Save the playlist to the database and close the dialog
  const handleSave = async () => {
    const payload = { loopId: orderSequence.toString() };
    try {
      const response = await axiosInstance.post(
        API_BASE_URL + "animation/loop-settings/change-loop",
        payload
      );
      if (response.status >= 200 && response.status <= 300) {
        const updatedPlaylist = await getAnimationPlaylist();
        setInitialOrderSequence([...orderSequence]);
        setInitialPlaylist([...updatedPlaylist]);
        setOpen(false);
        setTimeout(() => {
          toast.success("Animation removed successfully", {
            position: "top-right",
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
          });
        }, 2000);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Reset the playlist and close the dialog
  const handleReset = () => {
    setNewPlaylist([...initialPlaylist]); // Restore playlist
    setOrderSequence([...initialOrderSequence]); // Restore order sequence
  };

  // Main Category Select function
  const handleCategoryCheckboxChange = (
    index: number,
    checked: boolean
  ): void => {
    const selectedCategory = animationList[index];
    if (selectedCategory) {
      // if checked "True" add playlist
      if (checked) {
        const ids = selectedCategory.animationList
          .filter((item) => !item.checked)
          .map((item) => item.animationId)
          .join(", "); // Join IDs into a single string
        const payload = {
          // languageId: defaultLanguage,
          languageId: 50,
          webSiteId: 1,
          animationId: ids,
          checked: checked,
        };
        const updateAnimationDetail = async (payload: any) => {
          try {
            const response = await axiosInstance.post(
              API_BASE_URL + "animation/loop-settings/add",
              payload
            );
            if (response.status >= 200 && response.status <= 300) {
              const newAnimations = response.data.data.loopedVideos;
              setNewPlaylist((prevPlaylist) => {
                // Filter out duplicates by checking if the animation already exists in the playlist
                const animationsToAdd = newAnimations.filter(
                  (newAnimation: { animationId: string }) =>
                    !prevPlaylist.some(
                      (existingAnimation) =>
                        existingAnimation.animationId ===
                        newAnimation.animationId
                    )
                );

                // Add only the new animations to the playlist
                const updatedPlaylist = [...prevPlaylist, ...animationsToAdd];
                return updatedPlaylist;
              });
              getCategoryAnimation();
              getAnimationPlaylist();
              setTimeout(() => {
                toast.success("Animation added successfully", {
                  position: "top-right",
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: false,
                  draggable: false,
                });
              }, 2000);
            }
          } catch (err) {
            console.log(err);
          }
        };
        updateAnimationDetail(payload);
      }
      // if checked "False" remove playlist
      else {
        const ids = selectedCategory.animationList
          .filter((item) => item.checked)
          .map((item) => item.loopId)
          .join(", "); // Join IDs into a single string
        const payload = {
          loopId: ids.toString(),
        };
        const updateAnimationDetail = async (payload: any) => {
          try {
            const response = await axiosInstance.post(
              API_BASE_URL + "animation/loop-settings/delete",
              payload
            );

            if (response.status >= 200 && response.status <= 300) {
              getCategoryAnimation();
              getAnimationPlaylist();
              const animationIdsToRemove = response.data.data.loopedVideos.map(
                (animation: { loopId: string }) => animation.loopId
              );
              setNewPlaylist((prevPlaylist) => {
                // Remove animations from the playlist by filtering out matching IDs
                const updatedPlaylist = prevPlaylist.filter(
                  (animation) =>
                    !animationIdsToRemove.includes(animation.animationId)
                );
                return updatedPlaylist;
              });
              setTimeout(() => {
                toast.success("Animation deleted successfully", {
                  position: "top-right",
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: false,
                  draggable: false,
                });
              }, 2000);
            }
          } catch (err) {
            console.log(err);
          }
        };
        updateAnimationDetail(payload);
      }
    }

    const newData = [...animationList];
    const category = newData[index];
    const allChecked = category.animationList.every((sub) => sub.checked);
    category.animationList.forEach((sub) => {
      sub.checked = !allChecked;
    });
    setAnimationList(newData);
  };

  // Subcategory Select function
  const handleCheckboxChange = (
    index: number,
    _animationPId: number,
    subcategoryId: string,
    loopId: number | undefined,
    checked: boolean
  ): void => {

    if (checked) {
      const payload = {
        languageId: 50,
        webSiteId: 1,
        animationId: subcategoryId,
        checked: checked,
      };
      const updateAnimationDetail = async (payload: any) => {
        try {
          const response = await axiosInstance.post(
            API_BASE_URL + "animation/loop-settings/add",
            payload
          );

          if (response.status >= 200 && response.status <= 300) {
            const newAnimations = response.data.data.loopedVideos;
            if (newAnimations.length > 0) {
              setNewPlaylist((prevPlaylist) => {
                // Filter out duplicates by checking if the animation already exists in the playlist
                const animationsToAdd = newAnimations.filter(
                  (newAnimation: { animationId: string }) =>
                    !prevPlaylist.some(
                      (existingAnimation) =>
                        existingAnimation.animationId ===
                        newAnimation.animationId
                    )
                );
                // Add only the new animations to the playlist
                const updatedPlaylist = [...prevPlaylist, ...animationsToAdd];
                return updatedPlaylist;
              });

              getCategoryAnimation();
              getAnimationPlaylist();
              setTimeout(() => {
                toast.success("Animation added successfully", {
                  position: "top-right",
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: false,
                  draggable: false,
                });
              }, 2000);
            } else {
              toast.error(response.data.message, {
                position: "top-right",
                autoClose: 20000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              });
            }
          }
        } catch (err) {
          console.log(err);
        }
      };
      updateAnimationDetail(payload);
    } else {
      const payload = {
        loopId: loopId?.toString(),
      };
      const updateAnimationDetail = async (payload: any) => {
        try {
          const response = await axiosInstance.post(
            API_BASE_URL + "animation/loop-settings/delete",
            payload
          );
          if (response.status >= 200 && response.status <= 300) {
            getCategoryAnimation();
            getAnimationPlaylist();
            setTimeout(() => {
              toast.success("Animation removed successfully", {
                position: "top-right",
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
              });
            }, 2000);
          }
        } catch (err) {
          console.log(err);
        }
      };
      updateAnimationDetail(payload);
    }

    const newData = [...animationList];
    const category = newData[index];
    const subcategoryIndex = category.animationList.findIndex(
      (subcategory) => subcategory.animationId === subcategoryId
    );

    if (subcategoryIndex !== -1) {
      const subcategory = category.animationList[subcategoryIndex];
      subcategory.checked = !subcategory.checked;
      setAnimationList(newData);
    }
  };
  // New code
  const handleDropdownToggle = (index: number): void => {

    const newData = [...animationList];
    newData[index].expanded = !newData[index].expanded;
    setAnimationList(newData);
  };
  const selectedCount = animationList.reduce(
    (acc, category) =>
      acc + category.animationList.filter((sub) => sub.checked).length,
    0
  );
  // New Code
  const selectedCount1 = (categoryIndex: number): number => {
    return animationList[categoryIndex].animationList.filter(
      (sub) => sub.checked
    ).length;
  };

  // New COde
  const handleUnselectAll = () => {
    // Api code
    const uncheckedItems = animationList
      .flatMap((category) =>
        category.animationList
          .filter((animation) => animation.checked)
          .map((animation) => animation.loopId)
      )
      .join(",");

    const payload = {
      loopId: uncheckedItems.toString(),
    };
    const updateAnimationDetail = async (payload: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "animation/loop-settings/delete",
          payload
        );

        if (response.status >= 200 && response.status <= 300) {
          getCategoryAnimation();
          getAnimationPlaylist();
          setNewSelectedVideo(null); //clear the preview section
          setTimeout(() => {
            toast.success("Animation deleted successfully", {
              position: "top-right",
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: false,
              draggable: false,
            });
          }, 2000);
        }
      } catch (err) {
        console.log(err);
      }
    };
    updateAnimationDetail(payload);
  };

  // Shuffle the playlist
  const handleShuffle = () => {
    const shuffledPlaylist = [...newPlaylist].sort(() => Math.random() - 0.5);
    const ids = shuffledPlaylist.map((item) => item.id); // Extract IDs
    setOrderSequence(ids);

    setNewPlaylist(shuffledPlaylist); // Shuffle only the playlist
  };

  // Remove animation from playlist and uncheck in categories
  const handleRemoveFromPlaylist = (
    removeAnimId: string,
    removeAnimPId: number
  ): void => {
    // First, immediately update UI by removing from playlist and unchecking in categories
    setNewPlaylist((prevPlaylist) =>
      prevPlaylist.filter((item) => item.id !== removeAnimPId)
    );
    // Find and uncheck the corresponding checkbox in the category list
    let animationFound = false;
    setAnimationList((prevList) => {
      // Create a new array to ensure React detects the change
      return prevList.map((category) => {
        // Create a new array of animations for this category
        const updatedAnimations = category.animationList.map((animation) => {
          // If this is the animation we're looking for, uncheck it
          if (animation.animationId === removeAnimId) {
            animationFound = true;
            return { ...animation, checked: false };
          }
          return animation;
        });
        // Return a new category object with the updated animations
        return {
          ...category,
          animationList: updatedAnimations,
        };
      });
    });
    // Clear preview if the deleted item was being previewed
    if (newSelectedVideo && newSelectedVideo.includes(removeAnimId)) {
      setNewSelectedVideo(null);
    }
    if (newSelectedImage && newSelectedImage.includes(removeAnimId)) {
      setNewSelectedImage(null);
    }
    // Then make the API call
    const payload = {
      loopId: JSON.stringify(removeAnimPId),
    };
    const updateAnimationDetail = async (payload: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "animation/loop-settings/delete",
          payload
        );
        if (response.status >= 200 && response.status <= 300) {
          // Only refresh the playlist data if needed
          if (!animationFound) {
            // If we couldn't find the animation in our local state, refresh from server
            getCategoryAnimation();
            getAnimationPlaylist();
          }
          setTimeout(() => {
            toast.success("Animation deleted successfully", {
              position: "top-right",
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: false,
              draggable: false,
            });
          }, 2000);
        }
      } catch (err) {
        console.log(err);
        toast.error("Failed to remove item from playlist", {
          position: "top-right",
          autoClose: 3000,
        });
        // On error, refresh data from server to ensure consistency
        getCategoryAnimation();
        getAnimationPlaylist();
      }
    };
    updateAnimationDetail(payload);
  };
  const handleUploadClick = (type: "animation" | "image") => {
    setUploadType(type);
    setUploadDialogOpen(true);
  };

  const handleUploadSubmit = async (file: File, name: string) => {
    const selectedFile = file;
    const fileName = name;
    const formData = new FormData();
    formData.append("webSiteId", "1");
    formData.append("title", fileName);
    formData.append("file", selectedFile);
    const toastId = toast.info(t("uploading"), {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      progress: 0,
    });
    try {
      setIsUploading(true);
      const response = await axiosformdataInstance.post(
        `${API_BASE_URL}animation/loop-settings/uploadFile`,
        formData,
        {
          // Monitor the progress of the file upload
          onUploadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // Update toast progress
            toast.update(toastId, {
              progress: percentCompleted / 100,
              render: `Uploading... ${percentCompleted}%`,
            });
          },
        }
      );
      if (response.status >= 200 && response.status <= 300) {
        toast.update(toastId, {
          render: t("upload_successful"),
          type: "success", // Corrected type usage
          autoClose: 2000,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
        getAnimationPlaylist();
      }
    } catch (error) {
      console.error("Error uploading animation:", error);
      toast.update(toastId, {
        render: t("failed_to_upload_animation_please_try_again"),
        type: "error",
        autoClose: 3000,
        closeOnClick: true,
        draggable: true,
        isLoading: false, // Stop loading animation
      });
    } finally {
      setIsUploading(false);
    }
  };


  const handleSubcategoryClick = (
    url: string,
    type: string,
    customUrl?: string
  ) => {
    const isCustom = customUrl == null;
    setCustomEnable(isCustom);
    if (type === "animation" || type === "video") {
      setNewSelectedVideo(customUrl || url);
      setNewSelectedImage(null);
    } else if (type === "image") {
      setNewSelectedImage(customUrl || url);
      setNewSelectedVideo(null);
    } else {
      console.warn("Unknown type or missing URL");
    }
  };
  // Function to toggle fullscreen mode
  const toggleFullscreen = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIsFullscreen(1);
    } else {
      setIsFullscreen(0);
    }
  };

  // Function to open the new component in fullscreen
  const handlePlayLoop = () => {
    if (newPlaylist.length > 0) {
      const url = `/loopvideo`;
      window.open(url, "_blank");
    } else {
      toast.error(t("please_add_videos"), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };
  // Function to handle drag and drop
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setNewPlaylist((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);

        const updatedPlaylist = arrayMove(prev, oldIndex, newIndex);

        //  Update the orderSequence separately to trigger `isDisabled` update
        setOrderSequence(updatedPlaylist.map((item) => item.id));

        return updatedPlaylist;
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          overflow: "hidden",
          position: "relative",
          bgcolor: "#f8f9fa",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            gap: 0,
          }}
        >
          {/* Left Section - Categories */}
          <Box
            sx={{
              width: { xs: "100%", sm: "320px" },
              flexShrink: 0,
              borderRight: "1px solid rgba(0,0,0,0.08)",
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
            }}
          >
            {/* Categories Header */}
            <Box
              sx={{
                px: 2,
                py: 0.8,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#fff",
                height: "56px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    fontSize: "1rem",
                  }}
                >
                  {t("animations")} - {selectedCount}
                </Typography>
              </Box>

              {selectedCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleUnselectAll}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    minWidth: "90px",
                    fontSize: "0.8rem",
                    borderColor: "error.main",
                    color: "error.main",
                    height: "32px",
                    "&:hover": {
                      borderColor: "error.dark",
                      backgroundColor: "error.lighter",
                    },
                  }}
                >
                  {t("unselect_all")}
                </Button>
              )}
            </Box>

            {/* Categories List with improved scrolling */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "3px",
                },
              }}
            >
              {animationList?.map((category, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 1.5,
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  {/* Category Header */}
                  <Box
                    sx={{
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    <KeyboardArrowDownIcon
                      onClick={() => handleDropdownToggle(index)}
                      sx={{
                        transform: category.expanded
                          ? "rotate(0deg)"
                          : "rotate(-90deg)",
                        transition: "transform 0.2s ease",
                        color: "primary.main",
                        mr: 1,
                      }}
                    />
                    <Typography
                      onClick={() => handleDropdownToggle(index)}
                      sx={{
                        flex: 1,
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        color: "text.primary",
                      }}
                    >
                      {t(category.systemTagName)}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: category.expanded
                          ? "primary.lighter"
                          : "transparent",
                        borderRadius: "20px",
                        px: 1.5,
                        py: 0.25,
                        mr: 1,
                        color: "primary.main",
                        fontSize: "0.75rem",
                        visibility:
                          selectedCount1(index) === 0 ? "hidden" : "visible",
                      }}
                    >
                      {selectedCount1(index)}
                    </Box>
                    <Checkbox
                      checked={category.animationList.every(
                        (sub) => sub.checked
                      )}
                      indeterminate={
                        category.animationList.some((sub) => sub.checked) &&
                        !category.animationList.every((sub) => sub.checked)
                      }
                      onChange={(e) =>
                        handleCategoryCheckboxChange(index, e.target.checked)
                      }
                      sx={{
                        color: "primary.main",
                        "&.Mui-checked": {
                          color: "primary.main",
                        },
                      }}
                    />
                  </Box>
                  {/* Subcategories */}
                  {category.expanded && (
                    <Box
                      sx={{
                        pl: 4,
                        pr: 2,
                        pb: 1,
                        backgroundColor: "rgba(0,0,0,0.01)",
                      }}
                    >
                      {category.animationList
                        .sort((a, b) =>
                          t(a.animationTagName).localeCompare(
                            t(b.animationTagName)
                          )
                        )
                        .map((subcategory, subIndex) => (
                          <Box
                            key={subIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              py: 1,
                              px: 1,
                              borderBottom:
                                subIndex !== category.animationList.length - 1
                                  ? "1px solid rgba(0,0,0,0.05)"
                                  : "none",
                              transition: "background-color 0.2s ease",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.02)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                flex: 1,
                                fontSize: "0.875rem",
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {t(subcategory.animationTagName)}
                            </Typography>
                            <Checkbox
                              size="small"
                              checked={subcategory.checked}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  subcategory.animationPid,
                                  subcategory.animationId,
                                  subcategory.loopId,
                                  e.target.checked
                                )
                              }
                              sx={{
                                color: "primary.main",
                                "&.Mui-checked": {
                                  color: "primary.main",
                                },
                              }}
                            />
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Section - Controls and Playlist */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              maxWidth: "calc(100% - 320px)",
              bgcolor: "#fff",
            }}
          >
            {/* Top Controls Area */}
            <Box
              sx={{
                px: 2,
                py: 0.8,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                bgcolor: "#fff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                height: "56px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isFullscreen === 0 ? false : true}
                      onChange={toggleFullscreen}
                      size="small"
                    />
                  }
                  label={t("play_video_fullscreen")}
                  sx={{
                    my: 0,
                    flexShrink: 0,
                    "& .MuiTypography-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "nowrap",
                    overflow: "auto",
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handleUploadClick("animation")}
                    size="small"
                    sx={{
                      textTransform: "none",
                      minWidth: "fit-content",
                      whiteSpace: "nowrap",
                      height: "32px",
                      fontSize: "0.8rem",
                      flexShrink: 0,
                    }}
                  >
                    {t("upload_your_video")}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ImageIcon />}
                    onClick={() => handleUploadClick("image")}
                    size="small"
                    sx={{
                      textTransform: "none",
                      minWidth: "fit-content",
                      whiteSpace: "nowrap",
                      height: "32px",
                      fontSize: "0.8rem",
                      flexShrink: 0,
                    }}
                  >
                    {t("upload_your_image")}
                  </Button>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {t("show_images")}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={isImageSeconds || 5}
                      onChange={(e) =>
                        setIsImageSeconds(Number(e.target.value))
                      }
                      sx={{
                        width: 70,
                        "& .MuiInputBase-root": {
                          height: 32,
                          fontSize: "0.8rem",
                        },
                      }}
                    >
                      {[5, 10, 15, 20, 25, 30].map((seconds) => (
                        <MenuItem key={seconds} value={seconds}>
                          {seconds}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {t("seconds")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Main Content Area */}
            <Box
              sx={{
                flex: 1,
                p: 3,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 3,
                  height: "100%",
                }}
              >
                {/* Playlist Section */}
                <Box
                  sx={{
                    width: { xs: "100%", lg: "45%" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: { xs: "auto", lg: "80%" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t("my_playlist")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handlePlayLoop}
                        sx={{
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": { boxShadow: 3 },
                        }}
                      >
                        {t("play_loop")}
                      </Button>
                      <Tooltip title={t("shuffle_playlist")} arrow>
                        <IconButton
                          onClick={handleShuffle}
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            "&:hover": {
                              bgcolor: "primary.dark",
                            },
                            boxShadow: 1,
                          }}
                        >
                          <ShuffleIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box
                    className="myPlaylist_data"
                    sx={{
                      flex: 1,
                      // height: { xs: "300px", md: "400px", lg: "80%" }, // Different heights for different screen sizes
                      minHeight: { xs: "300px", lg: "0" },
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 1,
                      overflow: "auto",
                      bgcolor: "#fff",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={newPlaylist.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {newPlaylist.map((item, index) => (
                          <SortableItem
                            key={item.id}
                            item={item}
                            index={index}
                            handleRemove={handleRemoveFromPlaylist}
                            handleClick={(url, type, customUrl) =>
                              handleSubcategoryClick(url, type, customUrl)
                            }
                            className={
                              newSelectedVideo ===
                                (item.customUrl || item.videoUrl) ||
                              newSelectedImage ===
                                (item.customUrl || item.bannerUrl)
                                ? "selected"
                                : ""
                            }
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </Box>
                </Box>

                {/* Preview Section */}
                <Box
                  sx={{
                    width: { xs: "100%", lg: "55%" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: { xs: "auto", lg: "80%" },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("preview")}
                  </Typography>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "56.25%",
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      overflow: "hidden",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {newSelectedVideo ? (
                        <MediaPlayer
                          ref={videoRef}
                          key={newSelectedVideo}
                          id={`videoslider${newSelectedVideo}`}
                          data-vds-id={VIDEO_BASE_URL}
                          className="carouselvideo"
                          src={
                            customEnable
                              ? CUSTOM_UPLOAD_BASE_URL + newSelectedVideo
                              : VIDEO_BASE_URL + newSelectedVideo
                          }
                          viewType="video"
                          streamType="on-demand"
                          logLevel="warn"
                          crossOrigin
                          autoPlay
                          playsInline
                          controls={false}
                          style={{ width: "100%", height: "100%" }}
                        >
                          <MediaProvider>
                            <Poster className="vds-poster" />
                          </MediaProvider>
                          <DefaultVideoLayout icons={defaultLayoutIcons} />
                        </MediaPlayer>
                      ) : newSelectedImage ? (
                        <img
                          src={
                            customEnable
                              ? CUSTOM_UPLOAD_BASE_URL + newSelectedImage
                              : VIDEO_BASE_URL + newSelectedImage
                          }
                          alt="Selected"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            textAlign: "center",
                            margin: "0 auto",
                            display: "flex",
                            top: "50%",
                            left: "50%",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ color: "text.secondary" }}
                          >
                            {t("select_media_to_play")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Fixed Save/Reset Buttons */}
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 50 },
            right: { xs: 16, sm: 24 },
            display: "flex",
            flexWrap: "wrap", // Ensure buttons wrap on smaller screens
            justifyContent: { xs: "center", sm: "flex-end" }, // Center on small screens
            alignItems: "center",
            gap: 1.5,
            zIndex: 1200,
            bgcolor: "white",
            p: { xs: 1.5, sm: 1.5 }, // Adjust padding for different screen sizes
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            width: { xs: "calc(100% - 32px)", sm: "auto" }, // Full width on mobile
            left: { xs: 16, sm: "auto" }, // Center on mobile, right on larger screens
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleOpen("save")}
            disabled={isDisabled}
            sx={{
              flex: 1, // Make buttons equal width on small screens
              minWidth: { xs: "45%", sm: "80px" },
              maxWidth: { xs: "48%", sm: "80px" }, // Prevent buttons from taking full width
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              boxShadow: 2,
              "&:hover": { boxShadow: 3 },
            }}
          >
            {t("save")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOpen("reset")}
            disabled={isDisabled}
            sx={{
              flex: 1,
              minWidth: { xs: "45%", sm: "80px" },
              maxWidth: { xs: "48%", sm: "80px" },
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              boxShadow: 2,
              "&:hover": { boxShadow: 3 },
            }}
          >
            {t("reset")}
          </Button>
          <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle className="dialogtitle">
              {t("confirm_action")}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: "16px",
                  top: "16px",
                  border: "1px solid #be1e2d", // Red border
                  color: "#be1e2d", // Default text color
                  "&:hover": {
                    backgroundColor: "#be1e2d", // Full red background on hover
                    color: "#ffffff", // White icon color
                  },
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mt: 3, color: "black" }}>
                {/* Are you sure you want to save the playlist? */}
                {dialogText}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} className="cancelbtn">
                {t("no")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className="addbtn"
                sx={{ textTransform: "none" }}
                onClick={() => {
                  if (actionType === "save") {
                    handleSave(); // Save changes
                  } else if (actionType === "reset") {
                    handleReset(); // Reset playlist
                  }
                  setOpen(false); // Close dialog
                }}
              >
                {t("yes")}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>

      {/* Existing dialog... */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSubmit={handleUploadSubmit}
        type={uploadType}
      />
    </>
  );
};

type SortableItemProps = {
  item: AnimationList;
  index: number;
  handleRemove: (id: string, animationPId: number) => void;
  handleClick: (url: string, type: string, customUrl?: string) => void; // Updated signature
  className?: string; // Add className as optional
};

const SortableItem = ({
  item,
  handleRemove,
  handleClick,
  className,
}: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    justifyContent: "space-between",
    padding: "10px",
    margin: "10px 10px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  };

  return (
    <div ref={setNodeRef} style={style} className={className}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          cursor: "pointer",
          width: "100%",
        }}
      >
        <DragIndicatorIcon
          sx={{ mr: 1, cursor: "grab" }}
          {...listeners}
          {...attributes}
        />

        <Typography
          sx={{
            whiteSpace: { sm: "wrap", md: "wrap", lg: "nowrap" },
            width: "100%",
          }}
          onClick={() => {
            if (item.animationTagName !== null) {
              if (item.type === "animation" && item.videoUrl) {
                handleClick(item.videoUrl, "animation", "");
              } else if (item.type === "image" && item.bannerUrl) {
                handleClick(item.bannerUrl, "image", "");
              } else {
              }
            } else {
              if (item.type === "video" && item.customUrl) {
                handleClick(item.customUrl, "video", item.videoUrl);
              } else if (item.type === "image" && item.customUrl) {
                handleClick(item.customUrl, "image", item.bannerUrl);
              } else {
              }
            }
          }}
        >
          {item.animationTagName ? t(item.animationTagName) : item.customTitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          width: "100%",
          px: 1,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            mr: 1,
            display: "flex",
            alignItems: "center",
            color: className === "selected" ? "#fff" : "#1976d2",
          }}
        >
          {!item.animationTagName && item.type === "image" ? (
            <ImageIcon />
          ) : (
            <VideoCameraBackIcon />
          )}
        </Box>

        <Delete
          sx={{
            color: "#f88571",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleRemove(item.animationId, item.id);
          }}
        />
      </Box>
    </div>
  );
};

export default LoopSetting;
